<template>
  <div ref="menuButtonWrapperRef" @keydown.esc="closeOnEscape">
    <template v-if="$slots['menu-button-trigger']">
      <!-- A div trigger has none of a button's behaviour, so it says what it is and answers the same keys -->
      <div
        class="custom-trigger"
        :class="{ 'full-width': fullWidth }"
        ref="menuButtonRef"
        role="button"
        tabindex="0"
        aria-haspopup="true"
        :aria-expanded="isOpen"
        @click="toggle"
        @keydown.enter.prevent="toggle"
        @keydown.space.prevent="toggle"
      >
        <slot name="menu-button-trigger" />
      </div>
    </template>

    <Button
      v-else
      variant="outline"
      :class="{ 'full-width': fullWidth }"
      :size="buttonSize"
      v-bind="$attrs"
      ref="menuButtonRef"
      aria-haspopup="true"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <View class="button-prefix" gap="sm" direction="row" align="center" :grow="1">
        <slot name="trigger-button-prefix" />
        <span>{{ buttonLabel }}</span>
      </View>
      <Icon name="chevron-down" />
    </Button>

    <!-- On a phone a floating menu is cramped and easy to lose under a thumb; the sheet lists the same entries at full width -->
    <Modal
      v-if="asSheet"
      ref="sheetRef"
      position="end"
      frameless-body
      content-class="menu-sheet"
      @after-close="isOpen = false"
    >
      <template #header>{{ sheetTitle }}</template>
      <template #body>
        <div class="sheet-items" role="listbox">
          <slot />
        </div>
      </template>
      <template v-if="$slots['menu-footer']" #footer>
        <div class="sheet-footer">
          <slot name="menu-footer" />
        </div>
      </template>
    </Modal>

    <dialog v-else class="dialog" ref="dialogRef" :style="floatingStyles">
      <Listbox class="menu">
        <!-- Only the entries scroll, so the footer stays reachable however long the list is -->
        <div class="menu-items">
          <slot />
        </div>

        <div v-if="$slots['menu-footer']" class="menu-footer">
          <slot name="menu-footer" />
        </div>
      </Listbox>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, ReferenceElement, shift, size, useFloating } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import { computed, provide, ref, useTemplateRef } from 'vue';

import { screenMinSm, screenMobile } from '../../utils/tallo-breakpoints.util';
import { ButtonProps } from '../button/button-props.interface';
import Button from '../button/button.vue';
import Icon from '../icon.vue';
import Listbox from '../listbox/listbox.vue';
import Modal from '../modal.vue';
import View from '../view/view.vue';
import { menuButtonInjectionKey } from './menu-button.token';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<
    {
      buttonLabel?: string;
      fullWidth?: boolean;
      /** On a phone the entries open as a bottom sheet instead of a floating menu; `sheetTitle` heads it */
      mobileSheet?: boolean;
      sheetTitle?: string;
    } & ButtonProps
  >(),
  {
    size: 'large',
    fullWidth: false,
    mobileSheet: false,
    sheetTitle: '',
  },
);

// About four rows, the least a menu is useful at
const minMenuHeight = 192;

const dialogRef = ref<HTMLDialogElement | null>(null);
const sheetRef = ref<InstanceType<typeof Modal> | null>(null);
const isOpen = ref(false);
const asSheet = computed(() => props.mobileSheet && screenMobile.value);
const menuButtonWrapperRef = useTemplateRef<HTMLElement>('menuButtonWrapperRef');
const menuButtonRef = useTemplateRef<ReferenceElement>('menuButtonRef');
const buttonSize = computed(() => (screenMinSm.value ? props.size : 'medium'));

provide(menuButtonInjectionKey, {
  close,
});

// The sheet lives in the modals layer, outside this wrapper, so a tap inside it must not count as outside
onClickOutside(menuButtonWrapperRef, () => {
  if (!asSheet.value) {
    close();
  }
});

const { floatingStyles } = useFloating(menuButtonRef, dialogRef, {
  transform: false,
  strategy: 'absolute',
  whileElementsMounted: autoUpdate,
  placement: 'bottom-end',
  // Below the trigger, as a menu is expected to open, and above only when it does not fit there;
  // kept inside the viewport sideways, so a menu wider than a trigger at the edge does not spill off
  middleware: [
    offset(8),
    flip({ fallbackPlacements: ['top-end', 'bottom-start', 'top-start'] }),
    shift({ padding: 8 }),
    size({
      apply({ availableWidth, availableHeight, elements, rects }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
          maxWidth: `${Math.max(0, availableWidth)}px`,
          // Never squeezed below a few rows: a menu that cannot get them below overflows instead,
          // which is what makes the flip above open it on the side with room
          maxHeight: `${Math.max(minMenuHeight, availableHeight)}px`,
        });
      },
    }),
  ],
});

function toggle() {
  if (props.disabled) {
    return;
  }

  isOpen.value ? close() : show();
}

function show() {
  if (asSheet.value) {
    sheetRef.value?.show();
  } else {
    dialogRef.value?.show();
  }

  isOpen.value = true;
}

/** Escape belongs to the menu only while it is open; closed, it stays with whatever encloses it, such as a modal */
function closeOnEscape(event: KeyboardEvent) {
  if (!dialogRef.value?.open) {
    return;
  }

  event.stopPropagation();
  close();
}

function close() {
  // The sheet is a modal with a focus trap of its own, which hands focus back when it closes
  if (asSheet.value) {
    sheetRef.value?.close();
    isOpen.value = false;

    return;
  }

  // A non-modal dialog leaves focus wherever it was, so a keyboard user who picked an item (or
  // pressed Esc) would be dropped on the body; hand focus back to what opened the menu
  const focusWasInMenu = dialogRef.value?.contains(document.activeElement);

  dialogRef.value?.close();
  isOpen.value = false;

  if (focusWasInMenu) {
    // Whichever shape the trigger took: the Button renders one, a custom trigger is the div itself
    menuButtonWrapperRef.value?.querySelector<HTMLElement>(':scope > button, :scope > .custom-trigger')?.focus();
  }
}

defineExpose({
  show,
  close,
});
</script>

<style lang="scss">
// Teleported, so out of reach of scoped selectors: the entries and the footer run edge to edge, as
// a menu's do, and the footer keeps the divider a menu footer has
.modal-content.menu-sheet {
  .modal-footer {
    display: block;
    padding: 0;
  }
}
</style>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.full-width {
  width: 100%;
}

.custom-trigger {
  display: inline-block;

  &:focus-visible {
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: 2px;
  }
}

.dialog {
  margin: 0;
  padding: 0;
  border: none;
  border-radius: var(--t-border-radius-sm);
  box-shadow: var(--t-shadow-md);

  min-width: 16rem;
  z-index: 1;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  // Only while open, so the closed dialog keeps the browser's own display: none
  &[open] {
    display: flex;
  }
}

.menu {
  flex: 1;
  min-height: 0;
}

// Rows touch, as in the Listbox around them, so two filled rows never show a white line between them
.menu-items {
  display: flex;
  flex-direction: column;
  // Roughly seven rows: past that the list scrolls instead of running off the screen
  max-height: 21rem;
  overflow-y: auto;
  min-height: 0;

  // Nothing to list (only comment nodes from v-if/v-for): the footer stands alone, with no divider above it
  &:not(:has(*)) {
    display: none;

    + .menu-footer {
      border-top: none;
    }
  }

  // The wrappers between the list and its rows hand the list's corners down, so the rows at its ends can take them
  &:first-child {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
  }

  &:last-child {
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }
}

.menu-footer {
  flex-shrink: 0;
  border-top: 1px solid var(--t-color-border-secondary);
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
}

.sheet-items {
  display: flex;
  flex-direction: column;
  padding-block: var(--t-spacing-xs);
}

.sheet-footer {
  padding-block: var(--t-spacing-xs);
}
</style>
